import { MapPin, MessageCircle, Globe, BookOpen } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { WHATSAPP_URL, OFFICIAL_PHONE, OFFICIAL_DOMAIN } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const WHATSAPP_MSG_PARTS = [
  "Hello Akkshay ji, I want to book a consultation.",
  "Service: [e.g. Kundli Consultation]",
  "Mode: [Call / Chat / Video / In-Person]",
  "Preferred Date: [DD/MM/YYYY]",
  "City/Country: [Your Location]",
  "Concern: [Brief description]",
];

export default function ContactPage() {
  useSEO({
    title: "Contact Akkshay Narula | Astrologer, Numerologist & Vastu Consultant",
    description: "Contact Akkshay Narula for Vedic astrology, numerology, and Vastu consultations. Available via WhatsApp. Offices in Guwahati and Bangalore. Worldwide online consultations.",
    keywords: "contact astrologer India, Akkshay Narula contact, astrologer WhatsApp India, book astrology session India",
    canonical: "/contact",
  });
  return (
    <main data-testid="contact-page" style={{ backgroundColor: OFF_WHITE }}>
      {/* Header */}
      <section style={{ padding: "72px 0 56px", backgroundColor: "#F5F3EB", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>
            Get in Touch
          </p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, color: CHARCOAL, lineHeight: 1.1, maxWidth: "600px" }}>
            Contact & Booking
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", marginTop: "16px", lineHeight: 1.7, maxWidth: "500px" }}>
            All consultations are booked exclusively via WhatsApp. There are no open calendar slots.
          </p>
        </div>
      </section>

      {/* WhatsApp Booking Primary */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Instructions */}
            <div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "24px" }}>
                <MessageCircle size={20} style={{ color: GOLD }} />
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>
                  Primary Booking Channel
                </p>
              </div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "20px" }}>
                Book via WhatsApp
              </h2>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.75, marginBottom: "32px" }}>
                Send a WhatsApp message to begin your booking. Include the details below for faster confirmation.
              </p>
              <div style={{ border: `1px solid ${BORDER}`, padding: "28px 32px", marginBottom: "32px" }}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>
                  Message Template
                </p>
                {WHATSAPP_MSG_PARTS.map((line, i) => (
                  <p key={i} style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: i === 0 ? CHARCOAL : "#4A4A4A", lineHeight: 1.8, fontStyle: i > 0 ? "italic" : "normal" }}>
                    {line}
                  </p>
                ))}
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                style={{ backgroundColor: CHARCOAL, color: OFF_WHITE, padding: "16px 36px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: "10px", transition: "background-color 0.3s" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CHARCOAL; }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Open WhatsApp — +91 {OFFICIAL_PHONE}
              </a>
            </div>

            {/* Contact Details */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              <div style={{ padding: "32px", border: `1px solid ${BORDER}`, marginBottom: "-1px" }} data-testid="contact-whatsapp-info">
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <MessageCircle size={18} style={{ color: GOLD, marginTop: "2px", flexShrink: 0 }} />
                  <div>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "6px" }}>
                      WhatsApp (Official)
                    </p>
                    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 500, color: CHARCOAL }}>
                      +91 {OFFICIAL_PHONE}
                    </p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.8rem", color: "#8A8A8A", marginTop: "4px" }}>
                      Only official contact for bookings
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ padding: "32px", border: `1px solid ${BORDER}`, marginBottom: "-1px" }} data-testid="contact-location-info">
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <MapPin size={18} style={{ color: GOLD, marginTop: "2px", flexShrink: 0 }} />
                  <div>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "6px" }}>
                      Office Location
                    </p>
                    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 500, color: CHARCOAL }}>
                      Guwahati, Assam
                    </p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.8rem", color: "#8A8A8A", marginTop: "4px" }}>
                      In-person consultations by appointment only
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ padding: "32px", border: `1px solid ${BORDER}`, marginBottom: "-1px" }} data-testid="contact-website-info">
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <Globe size={18} style={{ color: GOLD, marginTop: "2px", flexShrink: 0 }} />
                  <div>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "6px" }}>
                      Official Website
                    </p>
                    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 500, color: CHARCOAL }}>
                      {OFFICIAL_DOMAIN}
                    </p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.8rem", color: "#8A8A8A", marginTop: "4px" }}>
                      The only official online presence
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ padding: "32px", border: `1px solid ${BORDER}` }} data-testid="contact-books-info">
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <BookOpen size={18} style={{ color: GOLD, marginTop: "2px", flexShrink: 0 }} />
                  <div>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "6px" }}>
                      Published Works
                    </p>
                    <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 500, color: CHARCOAL }}>
                      Author of 4 Books
                    </p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.8rem", color: "#8A8A8A", marginTop: "4px" }}>
                      On Astrology, Numerology & Vastu
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section style={{ padding: "56px 0", backgroundColor: "#F5F3EB" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div style={{ border: `1px solid ${BORDER}`, padding: "40px", display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ width: "3px", minHeight: "80px", backgroundColor: GOLD, flexShrink: 0 }} />
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "10px" }}>
                Important Note
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.92rem", color: "#4A4A4A", lineHeight: 1.75 }}>
                Akkshay Narula does not work through intermediaries, agents, or representatives. There are no promotional offers, discounts, or free consultations. If anyone contacts you claiming to represent him, it is a fraud. Verify before you pay.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
